import React, { memo, useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Icon } from "semantic-ui-react";
import { useLoginCheck } from "../../hooks/useLoginCheck";
import "../../styles/scss/monthlyLessonParticipants.scss";
import { slashAppMonthly } from "../../utils/slashAppMonthly";
import MonthlyLessonFormModal from "./MonthlyLessonFormModal";
import MonthlyShiftAdjustPanel from "./MonthlyShiftAdjustPanel";

// マンスリーレッスン（単発の有料レッスン）の参加者一覧。
// 開催回を主役にして、回の行を開くとその回の申込者が下に出る形にしている。
// 「この回に誰が来るか」を見るのが主な使い方なので、回と名簿を別の表に分けず
// ひとつにまとめた。回は毎月増えるため、開催予定 / 終了 で分けて月ごとに束ねる。
const WEEK = ["日", "月", "火", "水", "木", "金", "土"];

// 画面タブ。「シフト調整」は回の一覧ではなく、講師が出したシフトから
// 日程を決める画面（MonthlyShiftAdjustPanel）なので、絞り込みではなく
// 画面ごと切り替える。選んだタブは ?tab= に持たせ、リロードやリンク共有でも保つ。
const VIEW_LESSONS = "lessons";
const VIEW_SHIFT = "shift";
const VIEWS = [
  { key: VIEW_LESSONS, label: "レッスン一覧" },
  { key: VIEW_SHIFT, label: "シフト調整" },
];

// 「表示」の絞り込み（レッスン一覧タブの中だけ）。
const PHASES = [
  { key: "all", label: "すべて", empty: "マンスリーの回はありません" },
  {
    key: "upcoming",
    label: "開催予定",
    empty: "これから開催する回はありません",
  },
  { key: "past", label: "終了した回", empty: "終了した回はありません" },
];

function formatDate(iso) {
  if (!iso) return "—";
  const d = new Date(`${iso}T00:00:00`);
  return `${d.getMonth() + 1}/${d.getDate()}(${WEEK[d.getDay()]})`;
}

function formatDateTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(d.getMonth() + 1)}/${pad(d.getDate())} ${pad(
    d.getHours(),
  )}:${pad(d.getMinutes())}`;
}

// 申込の支払い状況。事前決済をやめたので「未払い」は離脱ではなく通常の状態。
// 請求前 → (開催30日前に請求) → 請求済み → (入金) → 入金済み、と進む。
// 返金済みは Stripe で全額返金された人（Webフックが付ける）。席は持たず、
// 一覧の末尾にだけ出る。
const payState = (p) => {
  if (p.payment_status === "refunded") {
    return { label: "返金済み", className: "state-badge is-refunded" };
  }
  if (p.payment_status === "paid") {
    return { label: "入金済み", className: "state-badge is-paid" };
  }
  if (p.invoice_sent_at) {
    return { label: "請求済み", className: "state-badge is-invoiced" };
  }
  return { label: "請求前", className: "state-badge" };
};

// 開催回の状態。左の色帯とバッジの色をこれで決める。
// 中止 > 開講確定 > 人数不足 > 最少人数クリア の順に強い。
//
// 判定前の回は申込人数をそのまま出し、足りているかは色で示す。
// 黄=その回の開講最少人数（既定3名）に届いていない / 緑=届いている。不足数（あと◯名）は
// 引き算した数より申込人数そのものの方が読みたい情報なので出さない。
const lessonState = (l) => {
  if (l.canceled_at) return { key: "ng", label: "中止" };
  if (l.confirmed_at) return { key: "ok", label: "開講確定" };
  const label = `${l.applied_count}名`;
  if (!l.meets_minimum) return { key: "warn", label };
  // 最少人数には届いたが、まだ開講判定（10時・14時・18時）を通っていない状態。
  // 「開講確定」(confirmed_at あり) とは別物なので、破線の枠で区別する。
  return { key: "ready", label };
};

// 検索の当たり判定。回の見出し（クラス名・講師・日付）に当たったときは
// その回の申込者を全員残す。申込者に当たったときはその人だけ残す。
const hitLesson = (l, kw) =>
  [
    l.name,
    l.instructor_name,
    l.instructor_username,
    l.studio_name,
    formatDate(l.date),
  ]
    .filter(Boolean)
    .some((v) => String(v).toLowerCase().includes(kw));

const hitParticipant = (p, kw) =>
  [p.name, p.mail, p.tel, String(p.id)]
    .filter(Boolean)
    .some((v) => String(v).toLowerCase().includes(kw));

const isoOf = (d) => {
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

// 月ごとに束ねる。開催予定 / 終了した回 などはタブで分けるので、ここは月だけ見る。
// APIは「次にやる回が先頭、終わった回はその下に新しい順」で返すので、
// 並べ替えはせず前から順に見出しを差し込むだけでよい。
function buildGroups(lessons) {
  const groups = [];
  lessons.forEach((l) => {
    const d = new Date(`${l.date}T00:00:00`);
    const month = `${d.getFullYear()}年 ${d.getMonth() + 1}月`;
    const last = groups[groups.length - 1];
    if (last && last.month === month) {
      last.items.push(l);
      return;
    }
    // 「すべて」では今月が 開催予定の先頭 と 終了した回の先頭 の2か所に出るので、
    // 月名だけだとキーが重なる。出てきた順番を足して一意にする。
    groups.push({ key: `${month}-${groups.length}`, month, items: [l] });
  });
  return groups;
}

// 出席の表示。講師がこの回の出席を1件でも登録したかで意味が変わる。
// 誰も登録していない回の「—」は欠席ではなく未登録なので、区別して出す。
const attendChip = (p, lessonHasAttendance) => {
  // 返金済みは席を持たないので、出欠は問わない
  if (p.payment_status === "refunded" && !p.attended_at)
    return { label: "—", className: "attend-chip is-none" };
  if (p.attended_at) return { label: "出席", className: "attend-chip is-on" };
  if (lessonHasAttendance)
    return { label: "欠席", className: "attend-chip is-off" };
  return { label: "未登録", className: "attend-chip is-none" };
};

// 展開したときに出る申込者の1行。
const ParticipantRow = memo(function ParticipantRow({ p, lesson, onCancel }) {
  const state = payState(p);
  const attend = attendChip(p, lesson.attended_count > 0);
  // キャンセルは原則お受けしないが、運営が個別に受けると判断したときに使う。
  // 触れるのは請求書を出す前だけ（開講決定の後でも可）。請求書を出した後は
  // Stripe の取り消しと返金が絡む。サーバーも同じ条件で弾く。
  const cancelable =
    !p.invoice_sent_at && p.payment_status === "pending" && !lesson.canceled_at;
  return (
    <div className="p-row">
      <div className="p-cell p-name">
        {p.name || "—"}
        {/* 会員としてログインして申し込んだ人。会員価格が効いたかは金額で分かる */}
        {p.is_member ? <span className="member-chip">会員</span> : null}
      </div>
      <div className="p-cell p-age">{p.age || "—"}</div>
      <div className="p-cell p-mail" title={p.mail}>
        {p.mail || "—"}
      </div>
      <div className="p-cell p-tel">{p.tel || "—"}</div>
      <div className="p-cell p-amount">&yen;{p.amount.toLocaleString()}</div>
      <div className="p-cell p-state">
        <span className={state.className}>
          <span className="dot" />
          {state.label}
        </span>
      </div>
      <div className="p-cell p-attend">
        <span
          className={attend.className}
          title={p.attended_at ? formatDateTime(p.attended_at) : undefined}
        >
          {attend.label}
        </span>
      </div>
      <div className="p-cell p-when">
        {p.payment_status === "paid" || p.payment_status === "refunded"
          ? formatDateTime(p.paid_at)
          : formatDateTime(p.invoice_sent_at)}
        {p.invoice_url ? (
          <a
            href={p.invoice_url}
            target="_blank"
            rel="noopener noreferrer"
            className="invoice-link"
          >
            請求書
          </a>
        ) : null}
      </div>
      <div className="p-cell p-act">
        {cancelable ? (
          <button
            type="button"
            className="p-cancel"
            onClick={() => onCancel(p, lesson)}
          >
            キャンセル
          </button>
        ) : null}
      </div>
    </div>
  );
});

// 開催回の1件。見出し行をクリックすると申込者が開く。
// 中止 / 開講判定のボタンは見出しの右に置き、クリックが展開に伝わらないよう止める。
const LessonItem = memo(function LessonItem({
  lesson,
  participants,
  isOpen,
  inDecisionWindow,
  inInvoiceWindow,
  isPast,
  onToggle,
  onDecide,
  onCancel,
  onCancelParticipant,
  onEdit,
  editable,
  busy,
}) {
  const state = lessonState(lesson);
  const d = new Date(`${lesson.date}T00:00:00`);
  // 判定前の回に加えて、開講確定済みで請求書が出ていない申込が残っている回でも
  // 押せる（Stripeが落ちていたときの出し直し）。
  //
  // 判定前の回は開催日の前日と当日だけ押せる（inDecisionWindow）。先の回を
  // 人数不足で締めると、まだ申し込むはずだった人を締め出してしまうため。
  // 開講確定済みで未請求が残る回は、請求書を出す時期（開催30日前から＝
  // inInvoiceWindow）に入っていれば押せる。それより前はまだ請求しない時期なので
  // 「未請求」が正常で、押しても請求書は出ない（サーバーも出さない）。
  // 普段は10:00/14:00/18:00のジョブが自動で判定・請求する。
  // 終わった回（isPast）には出さない。サーバーも弾く。
  const unbilled =
    lesson.unbilled_count ?? lesson.applied_count - lesson.invoiced_count;
  const decidable =
    !lesson.canceled_at &&
    ((lesson.confirmed_at && unbilled > 0 && inInvoiceWindow && !isPast) ||
      (!lesson.confirmed_at && inDecisionWindow));
  // 開講は決まったが、まだ請求書を出す時期ではない回。
  const invoicePending =
    lesson.confirmed_at && !lesson.canceled_at && !inInvoiceWindow;

  const stop = (e) => e.stopPropagation();

  return (
    <div className={`lesson-item is-${state.key} ${isOpen ? "is-open" : ""}`}>
      <div
        className="lesson-head"
        role="button"
        tabIndex={0}
        onClick={() => onToggle(lesson.id)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onToggle(lesson.id);
          }
        }}
      >
        <div className="date-box">
          <span className="dd">{d.getDate()}</span>
          <span className="ww">{WEEK[d.getDay()]}</span>
        </div>

        <div className="lesson-mid">
          <div className="line1">
            <span className="cls">{lesson.name}</span>
            {/* 講師名はクラス名と同じ文字色で、縦罫だけで区切る */}
            {lesson.instructor_name ? (
              <span className="ins">{lesson.instructor_name}</span>
            ) : null}
            <span className={`badge is-${state.key}`}>
              <span className="dot" />
              {state.label}
            </span>
          </div>
          <div className="line2">
            <span>
              {lesson.start_time}–{lesson.end_time}
            </span>
            <span className="sep">|</span>
            {/* 足りているかはバッジの色で分かる。ここは定員に対する埋まり具合を
                見る場所。開講最少人数は既定の3名と違う回だけ添える。 */}
            <span>
              申込 {lesson.applied_count}
              {lesson.capacity ? ` / ${lesson.capacity}` : "名"}
            </span>
            {lesson.min_participants && lesson.min_participants !== 3 ? (
              <>
                <span className="sep">|</span>
                <span>{lesson.min_participants}名で開講</span>
              </>
            ) : null}
            {lesson.invoiced_count > 0 ? (
              <>
                <span className="sep">|</span>
                <span>
                  請求済 {lesson.invoiced_count} ・ 入金 {lesson.paid_count}
                </span>
              </>
            ) : null}
            {invoicePending && lesson.invoice_from ? (
              <>
                <span className="sep">|</span>
                <span>
                  請求書は{Number(lesson.invoice_from.slice(5, 7))}/
                  {Number(lesson.invoice_from.slice(8, 10))}から送付
                </span>
              </>
            ) : null}
            {/* 出席は講師がアプリから付ける。開講した回だけ意味があるので、
                開講確定した回か、すでに誰か出席が付いている回にだけ出す。 */}
            {lesson.confirmed_at || lesson.attended_count > 0 ? (
              <>
                <span className="sep">|</span>
                <span>
                  出席 {lesson.attended_count} / {lesson.applied_count}
                </span>
              </>
            ) : null}
            {/* 終わった回なのに出席が1件も無い。このままだと給与計算が
                最低保証6,000円で計上する（実人数×単価にならない）。 */}
            {lesson.confirmed_at && isPast && lesson.attended_count === 0 ? (
              <span className="attend-warn">出席未登録</span>
            ) : null}
            {lesson.canceled_at ? (
              <>
                <span className="sep">|</span>
                <span>講師見舞金 2,500円</span>
              </>
            ) : null}
          </div>
        </div>

        <div className="lesson-actions" onClick={stop} onKeyDown={stop}>
          {editable ? (
            <button
              type="button"
              className="act act-ghost"
              onClick={() => onEdit(lesson)}
            >
              編集
            </button>
          ) : null}
          {decidable ? (
            <button
              type="button"
              className="act act-go"
              onClick={() => onDecide(lesson)}
              disabled={busy}
            >
              {busy
                ? "実行中…"
                : lesson.confirmed_at
                  ? `未請求 ${unbilled}名に請求書を送る`
                  : "開講判定を実行（請求書を送る）"}
            </button>
          ) : null}
          <button
            type="button"
            className={`act ${lesson.canceled_at ? "act-ghost" : "act-ng"}`}
            onClick={() => onCancel(lesson)}
          >
            {lesson.canceled_at ? "中止を取り消す" : "中止"}
          </button>
          <span className="chev" aria-hidden="true">
            ▶
          </span>
        </div>
      </div>

      {/* 閉じている間は中身を作らない。回が溜まっても重くならないようにする */}
      {isOpen ? (
        <div className="lesson-body">
          {participants.length === 0 ? (
            <p className="no-participant">該当する申込はありません</p>
          ) : (
            <div className="p-list">
              <div className="p-row p-head">
                <div className="p-cell">名前</div>
                <div className="p-cell">年齢</div>
                <div className="p-cell">メール</div>
                <div className="p-cell">電話</div>
                <div className="p-cell">金額</div>
                <div className="p-cell">状態</div>
                <div className="p-cell">出席</div>
                <div className="p-cell">請求/決済</div>
                <div className="p-cell" />
              </div>
              {participants.map((p) => (
                <ParticipantRow
                  key={p.id}
                  p={p}
                  lesson={lesson}
                  onCancel={onCancelParticipant}
                />
              ))}
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
});

function MonthlyLessonParticipantsPage() {
  useLoginCheck();

  const [lessons, setLessons] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();
  // 不明な値は一覧に倒す。既定の一覧タブは URL に出さない。
  const view =
    searchParams.get("tab") === VIEW_SHIFT ? VIEW_SHIFT : VIEW_LESSONS;
  const setView = useCallback(
    (next) => {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          if (next === VIEW_LESSONS) params.delete("tab");
          else params.set("tab", next);
          return params;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );
  // 終了した回は毎月積み上がるので既定では出さない。見たいときにタブで切り替える。
  const [phase, setPhase] = useState("upcoming"); // all | upcoming | past
  const [activeStudio, setActiveStudio] = useState(""); // "" = すべて
  // 講師は instructor_id で絞る。dancer_name（HINA など）は重複しうるので、
  // 名前で絞ると別人の回まで混ざる。タブの文字は一意な username を出す。
  const [activeInstructorId, setActiveInstructorId] = useState(""); // "" = すべて
  const [searchKeyword, setSearchKeyword] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  // 展開中の開催回。既定は全部閉じる（次にやる回だけ後で開く手もあるが、
  // まずは閉じた状態で全体を見渡せることを優先する）。
  const [openIds, setOpenIds] = useState([]);
  // 開講判定を実行中の回。連打で請求書が2通飛ぶのを防ぐ（サーバー側でも
  // 行ロックで弾いているが、ここで止めれば無駄なリクエスト自体が出ない）。
  const [decidingId, setDecidingId] = useState(null);
  // 講師が出したシフトのうち、まだ確定して通知していないものがあるか（slash-app）。
  const [hasUnconfirmed, setHasUnconfirmed] = useState(false);
  // 回の作成・編集モーダル。null = 閉じている / { lesson: null } = 新規 / { lesson } = 編集
  const [form, setForm] = useState(null);

  const fetchParticipants = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const apiUrl = process.env.REACT_APP_API_URL;
      const response = await fetch(
        `${apiUrl}/api/get_monthly_lesson_participants/`,
        { credentials: "include" },
      );
      if (!response.ok) {
        throw new Error(
          response.status === 401
            ? "ログインの有効期限が切れています。再度ログインしてください。"
            : "参加者の取得に失敗しました。",
        );
      }
      const data = await response.json();
      setLessons(data.lessons || []);
    } catch (e) {
      console.error(e);
      setError(e.message);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // 取れなくても一覧は使えるので、失敗は表示しない（コンソールにだけ残す）。
  const fetchShiftSummary = useCallback(async () => {
    try {
      const data = await slashAppMonthly.summary();
      setHasUnconfirmed(!!data.has_unconfirmed);
    } catch (e) {
      console.error(e);
    }
  }, []);

  useEffect(() => {
    fetchParticipants();
    fetchShiftSummary();
  }, [fetchParticipants, fetchShiftSummary]);

  // シフト調整で回を登録・確定したら、回の一覧と上部の表示を取り直す。
  const onShiftChanged = useCallback(() => {
    fetchParticipants();
    fetchShiftSummary();
  }, [fetchParticipants, fetchShiftSummary]);

  const toggleOpen = useCallback((id) => {
    setOpenIds((prev) =>
      prev.includes(id) ? prev.filter((v) => v !== id) : [...prev, id],
    );
  }, []);

  // 開催回の中止 / 中止取消。講師の見舞金（給与計算で1回2,500円）の判定に使う。
  // 中止にすると、未払いの請求書はサーバーが Stripe で無効化(void)して未請求に戻す。
  // 支払い済みの人への返金は Stripe のダッシュボードで別途行う。
  const toggleCanceled = useCallback(
    async (lesson) => {
      const next = !lesson.canceled_at;
      const label = `${formatDate(lesson.date)} ${lesson.start_time} ${lesson.name}`;
      const message = next
        ? `${label} を中止にします。\n\n` +
          `・講師には見舞金 2,500円 が給与に計上されます\n` +
          `・未払いの請求書は自動で無効化（void）します\n` +
          `・支払い済みの方への返金は Stripe のダッシュボードから別途行ってください\n\n` +
          `よろしいですか？`
        : `${label} の中止を取り消します。\n\n` +
          `・中止のお知らせメールを受け取った方には、中止取り消しのお知らせが自動で届きます\n` +
          `・${lesson.min_participants || 3}名以上いれば、次の確認時刻（10時・14時・18時）に開講が決まります\n\n` +
          `よろしいですか？`;
      if (!window.confirm(message)) return;

      // 申込者への連絡。すでにLINEや電話で伝えてあることもあるので、
      // 送るかどうかをここで選ばせる。メールは送信後に取り消せない。
      let notify = false;
      if (next && lesson.applied_count > 0) {
        notify = window.confirm(
          `申込者 ${lesson.applied_count}名 に中止のお知らせメールを送りますか？\n\n` +
            `OK … 送る\n` +
            `キャンセル … 送らない（中止の印だけ付ける）\n\n` +
            `※メールは送信後に取り消せません\n` +
            `※請求済み・入金済みの方には「ご返金いたします」と伝えます。` +
            `実際の返金は Stripe から別途行ってください`,
        );
      }

      try {
        const apiUrl = process.env.REACT_APP_API_URL;
        const res = await fetch(`${apiUrl}/api/set_monthly_lesson_canceled/`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            monthly_lesson_id: lesson.id,
            canceled: next,
            notify,
          }),
        });
        if (!res.ok) throw new Error("更新に失敗しました");
        const result = await res.json();

        // メールを送る選択をしたのに送れなかったときは黙って終わらせない。
        // ローカルなど送信が無効な環境だと0件で返ってくる。
        if (notify && !result.mail_enabled) {
          setError(
            "この環境ではメールを送れないため、中止の印だけ付けました。" +
              "申込者への連絡は別途行ってください。",
          );
        } else if (notify) {
          // 0名とだけ出ると、失敗したのか全員すでに届いているのか分からない。
          // すでに送信済みの人数も添えて区別できるようにする。
          const lines = [
            `${result.notified_count}名に中止のお知らせを送りました。`,
          ];
          if (result.already_sent_count) {
            lines.push(
              `${result.already_sent_count}名にはすでに送信済みのため送っていません。`,
            );
          }
          window.alert(lines.join("\n"));
        }

        // 中止取り消しのお知らせ。届かなかった人がいれば個別連絡が要る。
        if (!next && result.uncancel_failed_count) {
          window.alert(
            `${result.uncancel_failed_count}名に中止取り消しのお知らせを送れませんでした。\n` +
              `LINE等で個別にご連絡ください（担当者宛てにもメールで知らせています）。`,
          );
        } else if (!next && result.uncancel_notified_count) {
          window.alert(
            `${result.uncancel_notified_count}名に中止取り消しのお知らせを送りました。`,
          );
        }

        // 請求書の無効化の結果と、返金が要る人数。無効化に失敗した人は
        // 請求書が生きたまま（支払えてしまう）なので、名前を出して手作業を促す。
        if (next) {
          const lines = [];
          if (result.voided_count) {
            lines.push(
              `未払いの請求書 ${result.voided_count}件を無効化しました。`,
            );
          }
          if (result.void_failed?.length) {
            lines.push(
              `次の方の請求書を無効化できませんでした。Stripeのダッシュボードで無効化（void）してください:\n` +
                result.void_failed.join(" / "),
            );
          }
          if (result.void_skipped_count) {
            lines.push(
              `この環境では Stripe を操作しないため、請求書 ${result.void_skipped_count}件は無効化していません。`,
            );
          }
          if (result.void_paid?.length) {
            lines.push(
              `次の方は無効化の直前に支払いが済んでいました。返金の対象です:\n` +
                result.void_paid.join(" / "),
            );
          }
          if (result.paid_count) {
            lines.push(
              `支払い済みの方が ${result.paid_count}名います。Stripeのダッシュボードから返金してください` +
                `（返金すると一覧で「返金済み」になります）。`,
            );
          }
          if (lines.length) window.alert(lines.join("\n\n"));
        }
        await fetchParticipants();
      } catch (e) {
        console.error(e);
        setError(e.message || "更新に失敗しました");
      }
    },
    [fetchParticipants],
  );

  // 開講判定の手動実行。通常は 10:00 / 14:00 / 18:00台のジョブが自動で行う。
  // 判定前の回は、人数が足りていれば開講を確定して請求書を送り、足りなければ
  // 中止にして申込者へ知らせる。開講確定済みの回は、未請求の人にだけ請求書を送る。
  const decideLesson = useCallback(
    async (lesson) => {
      const label = `${formatDate(lesson.date)} ${lesson.start_time} ${lesson.name}`;
      const willOpen = lesson.meets_minimum;
      const message =
        `${label} の開講判定を今すぐ行います。\n\n` +
        `・申込 ${lesson.applied_count}名 / 必要 ${lesson.min_participants}名\n` +
        (lesson.confirmed_at
          ? `・開講確定済みです。まだ請求書が出ていない ${
              lesson.unbilled_count ??
              lesson.applied_count - lesson.invoiced_count
            }名に請求書メールを送ります\n`
          : willOpen
            ? `・開講を確定し、申込者 ${lesson.applied_count}名に請求書メールを送ります\n`
            : `・人数が足りないため中止にし、申込者へお知らせメールを送ります\n`) +
        `\nメールは送信後に取り消せません。よろしいですか？`;
      if (decidingId) return;
      if (!window.confirm(message)) return;
      setDecidingId(lesson.id);

      try {
        const apiUrl = process.env.REACT_APP_API_URL;
        const res = await fetch(`${apiUrl}/api/decide_monthly_lesson/`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ monthly_lesson_id: lesson.id }),
        });
        const result = await res.json();
        if (!res.ok || !result.success) {
          throw new Error(result.error || "開講判定に失敗しました");
        }
        if (result.failed_count) {
          setError(
            `${result.failed_count}件の請求書を発行できませんでした。` +
              `もう一度「開講判定を実行」を押すと未請求分だけ出し直せます。`,
          );
        }
        await fetchParticipants();
      } catch (e) {
        console.error(e);
        setError(e.message || "開講判定に失敗しました");
      } finally {
        setDecidingId(null);
      }
    },
    [fetchParticipants, decidingId],
  );

  // 申込を1件キャンセルする。席を空けるだけでメールは送らない。
  // 元に戻す導線は用意していないので、確認は必ず出す。
  const cancelParticipant = useCallback(
    async (participant, lesson) => {
      const message =
        `${participant.name} さんの申込をキャンセルします。\n\n` +
        `・${formatDate(lesson.date)} ${lesson.start_time} ${lesson.name}\n` +
        `・キャンセルは原則お受けしていません。個別に判断して受ける場合だけ取り消してください\n` +
        `・申込は ${lesson.applied_count - 1}名 になります\n` +
        (lesson.confirmed_at
          ? `・開講決定済みの回です\n` +
            `・人数が減っても開講は取り消されません\n`
          : "") +
        `・本人へのメールは送られません\n` +
        `・元に戻すことはできません\n\n` +
        `よろしいですか？`;
      if (!window.confirm(message)) return;

      try {
        const apiUrl = process.env.REACT_APP_API_URL;
        const res = await fetch(`${apiUrl}/api/cancel_monthly_customer/`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ monthly_customer_id: participant.id }),
        });
        const result = await res.json();
        if (!res.ok || !result.success) {
          throw new Error(result.error || "キャンセルに失敗しました");
        }
        await fetchParticipants();
      } catch (e) {
        console.error(e);
        setError(e.message || "キャンセルに失敗しました");
      }
    },
    [fetchParticipants],
  );

  // 店舗タブ。開催実績のある店舗だけ出す（今は木津川校のみ）。
  const studios = useMemo(() => {
    const seen = [];
    lessons.forEach((l) => {
      if (l.studio_name && !seen.includes(l.studio_name)) {
        seen.push(l.studio_name);
      }
    });
    return seen;
  }, [lessons]);

  // 講師タブ。店舗で絞ったあとの回から作る（その店舗に出ていない講師は出さない）。
  const studioFiltered = useMemo(
    () =>
      lessons.filter((l) => !activeStudio || l.studio_name === activeStudio),
    [lessons, activeStudio],
  );

  const instructors = useMemo(() => {
    const seen = [];
    studioFiltered.forEach((l) => {
      if (!l.instructor_id || seen.some((i) => i.id === l.instructor_id))
        return;
      seen.push({
        id: l.instructor_id,
        // username が無い古いデータでも選べるように dancer_name へ落とす
        label: l.instructor_username || l.instructor_name || "—",
      });
    });
    return seen;
  }, [studioFiltered]);

  // 店舗を切り替えて選択中の講師が居なくなったら「すべて」に戻す。
  // 残しておくと、タブが消えたまま0件になって理由が分からなくなる。
  useEffect(() => {
    if (
      activeInstructorId &&
      !instructors.some((i) => i.id === activeInstructorId)
    ) {
      setActiveInstructorId("");
    }
  }, [instructors, activeInstructorId]);

  // 店舗・講師・検索を適用した開催回。参加者も検索で絞り込んだ配列に差し替える。
  // 開催予定 / 終了した回 で分ける前の状態。タブの件数をここから数える。
  const matched = useMemo(() => {
    const kw = searchKeyword.trim().toLowerCase();
    return studioFiltered
      .filter(
        (l) => !activeInstructorId || l.instructor_id === activeInstructorId,
      )
      .map((l) => {
        if (!kw) return { lesson: l, participants: l.participants };
        if (hitLesson(l, kw))
          return { lesson: l, participants: l.participants };
        return {
          lesson: l,
          participants: l.participants.filter((p) => hitParticipant(p, kw)),
        };
      })
      .filter(({ participants }) => !kw || participants.length > 0);
  }, [studioFiltered, activeInstructorId, searchKeyword]);

  const todayIso = useMemo(() => isoOf(new Date()), []);

  // 開講判定を押せる範囲。サーバー側の decide_monthly_lesson_view と同じく
  // 「今日 〜 明日」だけ。ここを広げるとエラーになるボタンが並ぶ。
  const tomorrowIso = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    return isoOf(d);
  }, []);

  // 作成・編集で出すクラス名の候補（既存の回の名前）。
  const classNames = useMemo(
    () => [...new Set(lessons.map((l) => l.name))].sort(),
    [lessons],
  );

  const openEdit = useCallback((lesson) => setForm({ lesson }), []);

  // 保存したら一覧を取り直す。作った回が見えるよう「終了した回」表示なら切り替える。
  const onFormDone = useCallback(async () => {
    setForm(null);
    setPhase((prev) => (prev === "past" ? "upcoming" : prev));
    await Promise.all([fetchParticipants(), fetchShiftSummary()]);
  }, [fetchParticipants, fetchShiftSummary]);

  // 今日を含めて先が「開催予定」、それより前が「終了した回」。
  const phaseOf = useCallback(
    (l) => (l.date >= todayIso ? "upcoming" : "past"),
    [todayIso],
  );

  const phaseCount = useMemo(() => {
    const count = {
      all: matched.length,
      upcoming: 0,
      past: 0,
    };
    matched.forEach((v) => {
      count[phaseOf(v.lesson)] += 1;
    });
    return count;
  }, [matched, phaseOf]);

  const visibleLessons = useMemo(
    () =>
      phase === "all"
        ? matched
        : matched.filter((v) => phaseOf(v.lesson) === phase),
    [matched, phaseOf, phase],
  );

  const groups = useMemo(
    () => buildGroups(visibleLessons.map((v) => v.lesson)),
    [visibleLessons],
  );

  const participantsOf = useMemo(() => {
    const map = {};
    visibleLessons.forEach((v) => {
      map[v.lesson.id] = v.participants;
    });
    return map;
  }, [visibleLessons]);

  // 検索中は当たった回を開いたままにする（開くために毎回クリックさせない）。
  const searching = searchKeyword.trim().length > 0;

  // 今のタブが空のときに「反対側に何件あるか」を出すために使う。
  // 開催予定 ⇔ 終了した回 の間だけ。
  const otherPhase =
    phase === "upcoming" ? "past" : phase === "past" ? "upcoming" : null;
  const otherPhaseCount = otherPhase ? phaseCount[otherPhase] : 0;
  const currentPhase = PHASES.find((p) => p.key === phase);

  return (
    <div className="monthly-lesson-participants-page">
      {/* header */}
      <div className="page-header">
        <div>
          <p className="eyebrow">Monthly Lessons</p>
          <div className="title-row">
            <h1>マンスリーレッスン</h1>
          </div>
        </div>
        {/* 検索は回の一覧にしか効かないので、シフト調整では出さない */}
        {view === VIEW_LESSONS ? (
          <div className="header-tools">
            <button
              type="button"
              className="add-lesson-btn"
              onClick={() => setForm({ lesson: null })}
            >
              <Icon name="plus" />
              マンスリーレッスンを追加
            </button>
            <div className="search-bar">
              <Icon name="search" />
              <input
                type="text"
                placeholder="名前 / メール / 電話 / レッスン"
                value={searchKeyword}
                onChange={(e) => setSearchKeyword(e.target.value)}
              />
            </div>
          </div>
        ) : null}
      </div>

      {/* 画面タブ */}
      <div className="view-tabs" role="tablist">
        {VIEWS.map((v) => (
          <button
            key={v.key}
            type="button"
            role="tab"
            aria-selected={view === v.key}
            className={`view-tab ${view === v.key ? "active" : ""} ${
              v.key === VIEW_SHIFT && hasUnconfirmed ? "has-pending" : ""
            }`}
            onClick={() => setView(v.key)}
          >
            {v.label}
          </button>
        ))}
      </div>

      {error ? <div className="error-banner">{error}</div> : null}

      {/* 講師から提出されたシフトで、まだ確定して通知していないものがある */}
      {hasUnconfirmed ? (
        <div className="review-banner" role="status">
          <Icon name="warning sign" />
          <span className="review-banner-text">
            未確定のシフトがあります。日程を決めて講師へ通知してください。
          </span>
          {view !== VIEW_SHIFT ? (
            <button
              type="button"
              className="review-banner-go"
              onClick={() => setView(VIEW_SHIFT)}
            >
              シフト調整
            </button>
          ) : null}
        </div>
      ) : null}

      {view === VIEW_SHIFT ? (
        <MonthlyShiftAdjustPanel onChanged={onShiftChanged} />
      ) : (
        <>
          {/* 絞り込み */}
          <div className="filters">
            {/* 終了した回は既定で出さない。見たいときだけこちらに切り替える */}
            <div className="filter-row">
              <span className="filter-label">表示</span>
              <div className="tabs">
                {PHASES.map((p) => (
                  <button
                    key={p.key}
                    className={`tab ${phase === p.key ? "active" : ""}`}
                    onClick={() => {
                      setPhase(p.key);
                    }}
                  >
                    {p.label}
                    <span className="tab-count">{phaseCount[p.key]}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="filter-row">
              <span className="filter-label">店舗</span>
              <div className="tabs">
                <button
                  className={`tab ${activeStudio === "" ? "active" : ""}`}
                  onClick={() => {
                    setActiveStudio("");
                  }}
                >
                  すべて
                </button>
                {studios.map((s) => (
                  <button
                    key={s}
                    className={`tab ${activeStudio === s ? "active" : ""}`}
                    onClick={() => {
                      setActiveStudio(s);
                    }}
                  >
                    {s}校
                  </button>
                ))}
              </div>
            </div>

            {/* 講師タブ。1人しか居ないときは選ぶ意味が無いので出さない */}
            {instructors.length > 1 ? (
              <div className="filter-row">
                <span className="filter-label">講師</span>
                <div className="tabs">
                  <button
                    className={`tab ${activeInstructorId === "" ? "active" : ""}`}
                    onClick={() => {
                      setActiveInstructorId("");
                    }}
                  >
                    すべて
                  </button>
                  {instructors.map((i) => (
                    <button
                      key={i.id}
                      className={`tab ${
                        activeInstructorId === i.id ? "active" : ""
                      }`}
                      onClick={() => {
                        setActiveInstructorId(i.id);
                      }}
                    >
                      {i.label}
                    </button>
                  ))}
                </div>
              </div>
            ) : null}
          </div>

          {/* 開催回リスト。行を開くとその回の申込者が出る。 */}
          <div className="panel">
            {isLoading && lessons.length === 0 ? (
              <div className="empty-state">
                <p className="ja">読み込み中…</p>
                <p className="en">Loading</p>
              </div>
            ) : groups.length === 0 ? (
              <div className="empty-state">
                <p className="ja">
                  {searching ? "該当する申込がありません" : currentPhase.empty}
                </p>
                <p className="en">No Lessons</p>
                {/* 探しているものが反対のタブにあることは多いので、そこへ渡す */}
                {otherPhaseCount > 0 ? (
                  <button
                    type="button"
                    className="switch-phase"
                    onClick={() => {
                      setPhase(otherPhase);
                    }}
                  >
                    「{PHASES.find((p) => p.key === otherPhase).label}」に{" "}
                    {otherPhaseCount}件 あります
                  </button>
                ) : null}
              </div>
            ) : (
              <div className="lesson-groups">
                {groups.map((g) => {
                  return (
                    <div className="lesson-group" key={g.key}>
                      <div className="month-head">
                        <span className="month">{g.month}</span>
                      </div>
                      {g.items.map((l) => (
                        <LessonItem
                          key={l.id}
                          lesson={l}
                          participants={participantsOf[l.id] || []}
                          isOpen={searching || openIds.includes(l.id)}
                          inDecisionWindow={
                            l.date >= todayIso && l.date <= tomorrowIso
                          }
                          inInvoiceWindow={
                            !l.invoice_from || todayIso >= l.invoice_from
                          }
                          isPast={l.date < todayIso}
                          onToggle={toggleOpen}
                          onDecide={decideLesson}
                          onCancel={toggleCanceled}
                          onCancelParticipant={cancelParticipant}
                          onEdit={openEdit}
                          // 開講判定の済んだ回・当日以前の回は、請求書やお知らせの
                          // 内容と食い違うので編集させない（サーバーも同じ条件で弾く）。
                          editable={
                            l.date > todayIso &&
                            !l.canceled_at &&
                            !l.confirmed_at
                          }
                          busy={decidingId === l.id}
                        />
                      ))}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}

      {form ? (
        <MonthlyLessonFormModal
          lesson={form.lesson}
          minDate={tomorrowIso}
          classNames={classNames}
          onClose={() => setForm(null)}
          onDone={onFormDone}
        />
      ) : null}
    </div>
  );
}

export default MonthlyLessonParticipantsPage;
